import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaTag } from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';
import { useThemeClasses } from '../../hooks/useThemeClasses';

/**
 * Flipkart-style price block: Special Price, large ₹, MRP strike, % off, expandable breakdown.
 */
export default function PriceBreakdownCard({ pricing, embedded = false }) {
  const [showDetails, setShowDetails] = useState(false);
  const { isDark } = useThemeClasses();

  if (!pricing) return null;

  const {
    partPrice,
    mrp,
    gstRate,
    gstAmount,
    assuranceLevy,
    assuranceLabel,
    total,
  } = pricing;

  const hasDiscount = mrp && mrp > partPrice;
  const discountPct = hasDiscount
    ? Math.round(((mrp - partPrice) / mrp) * 100)
    : 0;

  return (
    <div
      className={
        embedded
          ? ''
          : isDark
            ? 'rounded-sm border border-slate-700/60 bg-slate-800/50 p-4 sm:p-5'
            : 'rounded-sm border border-slate-200 bg-white p-4 sm:p-5 shadow-sm'
      }
    >
      {hasDiscount && (
        <p
          className={`mb-1 text-xs font-medium ${
            isDark ? 'text-emerald-400' : 'text-[#388e3c]'
          }`}
        >
          Special price
        </p>
      )}

      <div className="flex flex-wrap items-baseline gap-2 sm:gap-3">
        <span
          className={`text-[28px] font-medium leading-none tracking-tight sm:text-[32px] ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
        >
          {formatPrice(partPrice)}
        </span>
        {hasDiscount && (
          <>
            <span
              className={`text-base line-through sm:text-lg ${
                isDark ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              {formatPrice(mrp)}
            </span>
            <span
              className={`text-base font-medium sm:text-lg ${
                isDark ? 'text-emerald-400' : 'text-[#388e3c]'
              }`}
            >
              {discountPct}% off
            </span>
          </>
        )}
      </div>

      <p className={`mt-1.5 text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
        Inclusive of all taxes
        {total != null && total !== partPrice ? (
          <>
            {' '}
            · Payable <span className="font-semibold">{formatPrice(total)}</span>
          </>
        ) : null}
      </p>

      {hasDiscount && (
        <p
          className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium ${
            isDark ? 'text-emerald-300/90' : 'text-[#388e3c]'
          }`}
        >
          <FaTag className="text-[10px]" />
          Extra ₹{Math.round(mrp - partPrice).toLocaleString('en-IN')} saved on this part
        </p>
      )}

      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className={`mt-2 flex items-center gap-1 text-xs font-medium hover:underline sm:text-sm ${
          isDark ? 'text-[#8ab4f8]' : 'text-[#2874f0]'
        }`}
      >
        {showDetails ? 'Hide price details' : 'Price details'}
        {showDetails ? <FaChevronUp className="text-[10px]" /> : <FaChevronDown className="text-[10px]" />}
      </button>

      {showDetails && (
        <dl
          className={`mt-3 space-y-2 rounded-sm border p-3 text-sm ${
            isDark
              ? 'border-slate-700/60 bg-slate-900/50'
              : 'border-slate-200 bg-slate-50'
          }`}
        >
          <div className="flex justify-between gap-4">
            <dt className={isDark ? 'text-slate-400' : 'text-slate-500'}>Part price</dt>
            <dd className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {formatPrice(partPrice)}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className={isDark ? 'text-slate-400' : 'text-slate-500'}>GST ({gstRate}%)</dt>
            <dd className={isDark ? 'text-white' : 'text-slate-900'}>{formatPrice(gstAmount)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className={`max-w-[70%] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {assuranceLabel || 'Platform fees'}
            </dt>
            <dd className={`shrink-0 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {formatPrice(assuranceLevy)}
            </dd>
          </div>
          <div
            className={`flex justify-between border-t pt-2 font-semibold ${
              isDark ? 'border-slate-600/80 text-white' : 'border-slate-200 text-slate-900'
            }`}
          >
            <dt>Total</dt>
            <dd className={isDark ? 'text-amber-400' : 'text-slate-900'}>{formatPrice(total)}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
